
import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { MicIcon, PlusIcon } from './Icons';

export default function NeuralHero({ onCreateProject, onStartGlobalVoice }: { onCreateProject: () => void, onStartGlobalVoice: () => void }) {
  const canvasContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!canvasContainerRef.current) return;

    let scene: THREE.Scene, camera: THREE.PerspectiveCamera, renderer: THREE.WebGLRenderer, particles: THREE.Points;
    const PARTICLE_COUNT = 3000; // Iets verminderd voor performance
    let time = 0;
    let animationFrameId: number;

    const init = () => {
      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
      camera.position.z = 5;

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setPixelRatio(window.devicePixelRatio);
      canvasContainerRef.current?.appendChild(renderer.domElement);

      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(PARTICLE_COUNT * 3);
      const colors = new Float32Array(PARTICLE_COUNT * 3);

      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const r = 4 + Math.random() * 2;
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);

        positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
        positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
        positions[i * 3 + 2] = r * Math.cos(phi);

        const mixedColor = new THREE.Color();
        const rand = Math.random();
        if (rand < 0.33) mixedColor.set(0x00f2ff); // Cyan
        else if (rand < 0.66) mixedColor.set(0x7000ff); // Purple
        else mixedColor.set(0xff00c8); // Magenta

        colors[i * 3] = mixedColor.r;
        colors[i * 3 + 1] = mixedColor.g;
        colors[i * 3 + 2] = mixedColor.b;
      }

      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

      const material = new THREE.PointsMaterial({
        size: 0.015,
        vertexColors: true,
        transparent: true,
        opacity: 0.8,
        blending: THREE.AdditiveBlending
      });

      particles = new THREE.Points(geometry, material);
      scene.add(particles);

      const onWindowResize = () => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      };

      const onMouseMove = (e: MouseEvent) => {
        const mouseX = (e.clientX / window.innerWidth) - 0.5;
        const mouseY = (e.clientY / window.innerHeight) - 0.5;
        camera.position.x += (mouseX - camera.position.x) * 0.05;
        camera.position.y += (-mouseY - camera.position.y) * 0.05;
        camera.lookAt(scene.position);
      };

      window.addEventListener('resize', onWindowResize);
      window.addEventListener('mousemove', onMouseMove);

      const animate = () => {
        animationFrameId = requestAnimationFrame(animate);
        time += 0.002;

        const posAttr = particles.geometry.attributes.position;
        const positions = posAttr.array as Float32Array;

        for (let i = 0; i < PARTICLE_COUNT; i++) {
          const ix = i * 3;
          const iy = i * 3 + 1;
          const iz = i * 3 + 2;

          positions[ix] += Math.sin(time + positions[iz] * 0.5) * 0.003;
          positions[iy] += Math.cos(time + positions[ix] * 0.5) * 0.003;
        }

        posAttr.needsUpdate = true;
        particles.rotation.y += 0.0008;
        particles.rotation.x += 0.0004;

        renderer.render(scene, camera);
      };

      animate();

      return () => {
        window.removeEventListener('resize', onWindowResize);
        window.removeEventListener('mousemove', onMouseMove);
        cancelAnimationFrame(animationFrameId);
        renderer.dispose();
      };
    };

    return init();
  }, []);

  return (
    <div className="relative w-full h-full overflow-hidden bg-[#030303] text-[#f0f0f0] font-['Inter']">
      <div ref={canvasContainerRef} id="canvas-container" className="absolute inset-0 z-0 brightness-110 contrast-125" />

      {/* Iridescent Gradients Overlay */}
      <div className="pointer-events-none absolute inset-0 z-10 mix-blend-screen opacity-40">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_20%_30%,rgba(0,242,255,0.15)_0%,transparent_50%)]" />
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_80%_70%,rgba(112,0,255,0.15)_0%,transparent_50%)]" />
      </div>

      <div className="pointer-events-none fixed inset-0 z-20 opacity-30 mix-blend-overlay">
        <svg className="w-full h-full">
          <filter id="noiseFilter">
            <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
          </filter>
          <rect width="100%" height="100%" filter="url(#noiseFilter)" />
        </svg>
      </div>

      {/* UI Overlay */}
      <main className="relative z-30 w-full h-full flex flex-col justify-between px-5 py-8 sm:px-16 sm:py-16 lg:px-32 lg:py-20 pointer-events-none">

        <nav className="flex justify-between items-start pointer-events-auto">
          <div className="flex items-center gap-6">
            <div className="relative w-8 h-8 border-2 border-white rotate-45 flex items-center justify-center">
              <div className="absolute inset-0.5 bg-gradient-to-br from-[#00f2ff] to-[#ff00c8] animate-pulse" />
            </div>
            <div className="text-left font-['Space_Mono'] uppercase">
              <span className="block font-bold text-sm tracking-wider">BDAT AI</span>
              <span className="text-[10px] opacity-50 tracking-[0.2em]">BDAT = Business Data & Agent Toolkit</span>
            </div>
          </div>

          <div className="text-right font-['Space_Mono'] text-[10px] uppercase tracking-widest space-y-2">
            {/* Right side nav content removed as per user request (was Speech Kern) */}
          </div>
        </nav>
        {/* Hero Section */}
        <section className="max-w-4xl pointer-events-auto">
          <h1 className="text-5xl sm:text-7xl md:text-[5.5rem] font-extrabold leading-[0.9] tracking-tighter mb-8 bg-gradient-to-b from-white via-white to-white/40 bg-clip-text text-transparent animate-[glitch_3s_infinite]">
            BDAT<br />Autonomous
          </h1>
          <p className="text-sm sm:text-base text-white/60 max-w-xl mb-10 leading-relaxed font-light">
            Business Data & Agent Toolkit. Jouw autonome sales workforce die leert van elk gesprek. BDAT bouwt voort op elke interactie om strategische groei te realiseren.
          </p>

          <div className="flex flex-wrap gap-4">
            <button
              onClick={onCreateProject}
              className="group flex items-center gap-3 pl-2 pr-6 py-2 bg-white text-black rounded-full font-bold hover:bg-[#00f2ff] transition-all hover:scale-105 active:scale-95 shadow-[0_0_30px_rgba(255,255,255,0.1)] border border-white"
            >
              <div className="w-8 h-8 bg-black rounded-full text-white flex items-center justify-center group-hover:bg-white group-hover:text-black transition-colors">
                <PlusIcon />
              </div>
              <span className="uppercase tracking-widest text-[10px]">Nieuw Project</span>
            </button>
          </div>
        </section>

        {/* Footer Section */}
        <footer className="flex justify-between items-end pointer-events-auto">
          <div className="font-['Space_Mono'] text-[9px] text-white/30 flex gap-8 tracking-widest uppercase">
            <div>SYSTEM: ONLINE</div>
            <div>GLOBAL MEMORY: SYNCED</div>
          </div>
        </footer>
      </main>

      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes glitch {
          0% { transform: translate(0); }
          1% { transform: translate(-2px, 1px); }
          2% { transform: translate(2px, -1px); }
          3% { transform: translate(0); }
          100% { transform: translate(0); }
        }
      `}} />
    </div>
  );
}
